/**
 * Flat Aurora (Zana violet) fleet admin settings overlay.
 * Visual redesign of the BusTracking `showSettings` surface — same handlers, no QR.
 */
import React, { useEffect, useMemo, useState } from "react";
import SchoolLocationMap, { CAMPUS_FENCE_RADIUS_M } from "./fleet/SchoolLocationMap";

const PAGE = "#FAF9FE";
const INK = "#1B1533";
const INK2 = "#5A5470";
const MUTED = "#8E86A8";
const BORDER = "#E9E5F3";
const BORDER_V = "#DED8EE";
const TINT = "#F1ECFE";
const PANEL = "#F4F1FA";
const ACCENT = "#6F4FF0";
const ACCENT_TEXT = "#5B4FA8";
const GRAD = "linear-gradient(135deg, #9B7CFF, #6F4FF0)";
const GREEN = "#54bd46";
const GREEN_BG = "#eaf7e7";
const GREEN_BD = "#cfe9c9";
const GREEN_TXT = "#3da233";
const AMBER_BG = "#fff3e0";
const AMBER_TXT = "#b5731f";
const DANGER = "#c2453f";
const DANGER_BD = "#f3d5d5";
const ZANA_BAND = "linear-gradient(120deg, #241A44 0%, #1B1233 55%, #171029 100%)";

const cardShadow = "0 4px 14px -4px rgba(27,36,48,0.12), 0 1px 3px rgba(27,36,48,0.06)";
const pillGlow = "0 18px 44px -16px rgba(111,79,240,0.5)";

export type FleetSettingsInvite = {
  id: string;
  email: string;
  role: string;
  used: boolean;
  usedAt?: unknown;
  createdAt?: { toMillis?: () => number } | null;
  superseded?: boolean;
};

export type FleetSettingsUser = {
  id: string;
  email: string;
  displayName: string;
  role: string;
};

export type FleetSettingsBus = {
  id: string;
  liveLocationUpdatedAt?: number | null;
};

export type FleetAdminSettingsProps = {
  orgCode: string;
  orgDisplayName: string;
  userEmail?: string | null;
  /** Campus / depot pin. View only — PEAR console is the write path. */
  schoolLocation: { lat: number; lng: number } | null;
  /** Saves a new campus pin. Absent → the card stays view-only. */
  onSaveSchoolLocation?: (p: { lat: number; lng: number }) => Promise<void>;
  schoolLocationSaving?: boolean;
  buses: FleetSettingsBus[];
  fleetUsers: FleetSettingsUser[];
  nowMs: number;
  signupCode: string | null;
  signupCodeExpiresAt: number | null;
  onGenerateSignupCode: () => void | Promise<void>;
  parentMapEnabled: boolean;
  parentMapSaving: boolean;
  onToggleParentMap: () => void | Promise<void>;
  legacyFuelFlowEnabled: boolean;
  tripCompleteSaving: boolean;
  onToggleLegacyFuelFlow: () => void | Promise<void>;
  showTermDates?: boolean;
  termStartDate?: string;
  termEndDate?: string;
  onSaveTermDates?: (start: string, end: string) => void | Promise<void>;
  inviteAdminEmail: string;
  onInviteAdminEmailChange: (v: string) => void;
  onInviteAdmin: () => void | Promise<void>;
  adminInviteSending: boolean;
  adminInviteStatus: "idle" | "success" | "error";
  adminInviteError?: string;
  /** Emails on the org's pendingAdminInvites list (not yet claimed). */
  pendingAdminInvites?: string[];
  /** Claimed admins on the org doc (adminEmails). */
  adminEmails?: string[];
  /** Primary owner email (organizations.adminEmail). */
  primaryAdminEmail?: string;
  onResendAdminInvite?: (email: string) => void | Promise<void>;
  inviteRole: "parent" | "bus_driver";
  onInviteRoleChange: (r: "parent" | "bus_driver") => void;
  inviteEmails: string[];
  onRemoveInviteEmail: (em: string) => void;
  inviteDraft: string;
  onInviteDraftChange: (v: string) => void;
  onInviteDraftKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBulkInvite: () => void | Promise<void>;
  bulkInviting: boolean;
  sentInvites: FleetSettingsInvite[];
  onResendInvite: (email: string, role: string) => void | Promise<void>;
  resendingEmail: string | null;
  onBack: () => void;
  onSignOut: () => void;
};

function initials(emailOrName: string): string {
  const s = (emailOrName || "").trim();
  if (!s) return "?";
  if (s.includes("@")) {
    const local = s.split("@")[0] || s;
    const parts = local.split(/[._\-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return local.slice(0, 2).toUpperCase();
  }
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function isDriverRole(role: string) {
  return ["Driver", "bus_driver", "driver"].includes(role);
}
function isParentRole(role: string) {
  return ["Parent", "client", "parent"].includes(role);
}

function isAdminRole(role: string) {
  const r = (role || "").toLowerCase().replace(/\s+/g, "_");
  return r === "fleet_admin" || r === "admin" || r === "owner";
}

function TeamBar({
  email,
  subtitle,
  action,
}: {
  email: string;
  subtitle: string;
  action?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 11,
        padding: "11px 14px",
        borderRadius: 14,
        background: PANEL,
        border: `1px solid ${BORDER}`,
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          background: TINT,
          display: "grid",
          placeItems: "center",
          flexShrink: 0,
          fontSize: 11.5,
          fontWeight: 800,
          color: ACCENT_TEXT,
        }}
      >
        {initials(email)}
      </span>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <span style={{ fontSize: 13, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {email}
        </span>
        <span style={{ fontSize: 11.5, color: MUTED }}>{subtitle}</span>
      </div>
      {action}
    </div>
  );
}

function formatCountdown(msLeft: number): string {
  if (msLeft <= 0) return "0:00";
  const totalSec = Math.ceil(msLeft / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const eyebrow: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.9px",
  textTransform: "uppercase",
  color: INK2,
};

const cardBase: React.CSSProperties = {
  borderRadius: 20,
  background: "#fff",
  border: `1px solid ${BORDER}`,
  boxShadow: cardShadow,
  padding: 24,
  display: "flex",
  flexDirection: "column",
};

/** Invite rows stay inside the card instead of stretching the settings grid. */
const inviteScroll: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 7,
  overflowY: "auto",
  overflowX: "hidden",
  minHeight: 0,
  overscrollBehavior: "contain",
};

function Toggle({
  on,
  disabled,
  onClick,
  onColor = GREEN,
}: {
  on: boolean;
  disabled?: boolean;
  onClick: () => void;
  onColor?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      style={{
        width: 48,
        height: 27,
        borderRadius: 999,
        background: on ? onColor : "#DED8EE",
        border: "none",
        padding: 3,
        display: "flex",
        justifyContent: on ? "flex-end" : "flex-start",
        cursor: disabled ? "default" : "pointer",
        flexShrink: 0,
        transition: "background 0.2s",
      }}
    >
      <span
        style={{
          width: 21,
          height: 21,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(27,36,48,0.25)",
          display: "block",
        }}
      />
    </button>
  );
}

function ParentMapPreview({ mode, active }: { mode: "on" | "off"; active: boolean }) {
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7, minWidth: 0, opacity: active ? 1 : 0.55 }}>
      {mode === "on" ? (
        <div
          style={{
            height: 100,
            borderRadius: 14,
            border: active ? `2px solid ${ACCENT}` : `1px solid ${BORDER}`,
            overflow: "hidden",
            position: "relative",
            background: "#eef1f6",
          }}
        >
          <svg viewBox="0 0 150 100" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} fill="none" aria-hidden>
            <rect width="150" height="100" fill="#f2f4f8" />
            <path d="M-5 32 L54 26 L104 40 L155 30" stroke="#fff" strokeWidth="7" />
            <path d="M-5 32 L54 26 L104 40 L155 30" stroke={GREEN} strokeWidth="2.4" />
            <path d="M28 -5 L36 46 L24 106" stroke="#fff" strokeWidth="7" />
            <path d="M28 -5 L36 46 L24 106" stroke={GREEN} strokeWidth="2.4" />
            <path d="M104 -5 L112 44 L132 106" stroke="#fff" strokeWidth="6" />
            <path d="M104 -5 L112 44 L132 106" stroke={GREEN} strokeWidth="2" />
            <ellipse cx="78" cy="80" rx="36" ry="14" fill="#e0eddb" />
          </svg>
          <div
            style={{
              position: "absolute",
              top: 30,
              left: 62,
              width: 20,
              height: 20,
              borderRadius: "50% 50% 50% 0",
              transform: "rotate(-45deg)",
              background: GRAD,
              boxShadow: "0 5px 12px -2px rgba(111,79,240,0.8)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: 6,
              left: 6,
              display: "flex",
              alignItems: "center",
              gap: 4,
              height: 18,
              padding: "0 8px",
              borderRadius: 999,
              background: "#fff",
            }}
          >
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: GREEN }} />
            <span style={{ fontSize: 8, fontWeight: 800, color: INK }}>LIVE</span>
          </div>
        </div>
      ) : (
        <div
          style={{
            height: 100,
            borderRadius: 14,
            border: `1px solid ${BORDER}`,
            background: PANEL,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            padding: "0 12px",
          }}
        >
          <span style={{ fontSize: 22 }} aria-hidden>🚌</span>
          <span style={{ fontSize: 11, fontWeight: 700, textAlign: "center", color: INK2 }}>On board</span>
          <span style={{ fontSize: 9.5, textAlign: "center", color: MUTED }}>Status only, no map</span>
        </div>
      )}
      <span
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: "0.5px",
          textAlign: "center",
          color: mode === "on" ? ACCENT_TEXT : MUTED,
        }}
      >
        {mode === "on" ? "ON · LIVE MAP" : "OFF · STATUS ONLY"}
      </span>
    </div>
  );
}

export default function FleetAdminSettings(props: FleetAdminSettingsProps) {
  const {
    orgCode,
    orgDisplayName,
    userEmail,
    schoolLocation,
    onSaveSchoolLocation,
    schoolLocationSaving = false,
    buses,
    fleetUsers,
    nowMs,
    signupCode,
    signupCodeExpiresAt,
    onGenerateSignupCode,
    parentMapEnabled,
    parentMapSaving,
    onToggleParentMap,
    legacyFuelFlowEnabled,
    tripCompleteSaving,
    onToggleLegacyFuelFlow,
    showTermDates = false,
    termStartDate = "",
    termEndDate = "",
    onSaveTermDates,
    inviteAdminEmail,
    onInviteAdminEmailChange,
    onInviteAdmin,
    adminInviteSending,
    adminInviteStatus,
    adminInviteError,
    pendingAdminInvites = [],
    adminEmails = [],
    primaryAdminEmail = "",
    onResendAdminInvite,
    inviteRole,
    onInviteRoleChange,
    inviteEmails,
    onRemoveInviteEmail,
    inviteDraft,
    onInviteDraftChange,
    onInviteDraftKeyDown,
    onBulkInvite,
    bulkInviting,
    sentInvites,
    onResendInvite,
    resendingEmail,
    onBack,
    onSignOut,
  } = props;

  const [tick, setTick] = useState(() => Date.now());
  const [copied, setCopied] = useState(false);
  /* The pin being edited, or null when not editing. Held apart from the
     saved value so Cancel is a real cancel and Save is a deliberate act. */
  const [locationDraft, setLocationDraft] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [termStartDraft, setTermStartDraft] = useState(termStartDate);
  const [termEndDraft, setTermEndDraft] = useState(termEndDate);
  const [termSaving, setTermSaving] = useState(false);
  const [termErr, setTermErr] = useState<string | null>(null);

  useEffect(() => {
    setTermStartDraft(termStartDate);
    setTermEndDraft(termEndDate);
  }, [termStartDate, termEndDate]);

  useEffect(() => {
    if (!signupCodeExpiresAt) return;
    const id = window.setInterval(() => setTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [signupCodeExpiresAt]);

  const codeLive = !!(signupCode && signupCodeExpiresAt && signupCodeExpiresAt > tick);
  const msLeft = codeLive && signupCodeExpiresAt ? Math.max(0, signupCodeExpiresAt - tick) : 0;
  const codeProgress = codeLive ? Math.min(1, msLeft / (15 * 60 * 1000)) : 0;

  const drivers = useMemo(() => fleetUsers.filter((u) => isDriverRole(u.role)), [fleetUsers]);
  const parents = useMemo(() => fleetUsers.filter((u) => isParentRole(u.role)), [fleetUsers]);
  const onRoute = useMemo(
    () => buses.filter((b) => b.liveLocationUpdatedAt != null && nowMs - (b.liveLocationUpdatedAt || 0) < 60_000).length,
    [buses, nowMs],
  );
  const pendingParentInvites = useMemo(
    () => sentInvites.filter((i) => !i.superseded && !i.used && i.role === "parent").length,
    [sentInvites],
  );

  const fleetTitle = (orgDisplayName || orgCode || "Fleet").toUpperCase();
  const avatar = initials(userEmail || orgDisplayName || "A");

  const inviteRows = useMemo(() => {
    return sentInvites
      .filter((i) => !i.superseded && i.role === inviteRole)
      .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
  }, [sentInvites, inviteRole]);
  const joined = inviteRows.filter((r) => r.used).length;
  const joinPct = inviteRows.length ? Math.round((joined / inviteRows.length) * 100) : 0;

  const meEmail = (userEmail || "").trim().toLowerCase();
  const ownerEmail = (primaryAdminEmail || "").trim().toLowerCase();
  const adminTeam = useMemo(() => {
    type Kind = "owner" | "admin" | "pending";
    const rows: { email: string; kind: Kind }[] = [];
    const seen = new Set<string>();
    const add = (raw: string, kind: Kind) => {
      const email = String(raw || "").trim().toLowerCase();
      if (!email || !email.includes("@") || seen.has(email)) return;
      seen.add(email);
      rows.push({ email, kind });
    };

    if (ownerEmail) add(ownerEmail, "owner");
    for (const e of adminEmails) add(e, ownerEmail && String(e).trim().toLowerCase() === ownerEmail ? "owner" : "admin");
    for (const u of fleetUsers) {
      if (!isAdminRole(u.role)) continue;
      add(u.email || "", ownerEmail && (u.email || "").trim().toLowerCase() === ownerEmail ? "owner" : "admin");
    }
    if (meEmail) add(meEmail, ownerEmail && meEmail === ownerEmail ? "owner" : "admin");
    for (const e of pendingAdminInvites) add(e, "pending");
    return rows;
  }, [adminEmails, fleetUsers, meEmail, ownerEmail, pendingAdminInvites]);

  function resendBtn(email: string, onClick: () => void) {
    const busy = resendingEmail === email;
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        style={{
          height: 26,
          padding: "0 13px",
          borderRadius: 999,
          background: TINT,
          border: "none",
          fontSize: 11,
          fontWeight: 800,
          color: ACCENT_TEXT,
          cursor: busy ? "default" : "pointer",
          flexShrink: 0,
        }}
      >
        {busy ? "Sending…" : "Resend"}
      </button>
    );
  }

  async function copyCode() {
    if (!signupCode) return;
    try {
      await navigator.clipboard.writeText(signupCode);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: PAGE,
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        fontFamily: "Geist, system-ui, sans-serif",
        color: INK,
      }}
    >
      {/* Top bar */}
      <div
        style={{
          height: 58,
          flexShrink: 0,
          background: "rgba(252,249,244,0.95)",
          borderBottom: `1px solid ${BORDER}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 30px",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            background: "none",
            border: "none",
            cursor: "pointer",
            color: INK,
            fontSize: 13.5,
            fontWeight: 700,
            padding: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path d="M10 12L6 8l4-4" stroke={INK2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back to Dashboard
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <span style={{ fontSize: 12.5, color: MUTED }}>{userEmail || ""}</span>
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: GRAD,
              display: "grid",
              placeItems: "center",
              fontSize: 11,
              fontWeight: 800,
              color: "#fff",
            }}
          >
            {avatar}
          </span>
        </div>
      </div>

      {/* Dark header band — no card overlap */}
      <div style={{ position: "relative", overflow: "hidden", background: ZANA_BAND, padding: "28px 0 28px", flexShrink: 0 }}>
        <div
          style={{
            position: "absolute",
            top: -140,
            right: -60,
            width: 420,
            height: 420,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(155,124,255,0.34), transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -180,
            left: 180,
            width: 340,
            height: 340,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(31,196,238,0.16), transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div style={{ position: "relative", maxWidth: 960, margin: "0 auto", padding: "0 30px" }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: "1.3px", color: "#C4B3FF", marginBottom: 8 }}>
            FLEET SETTINGS
          </div>
          <div
            style={{
              fontFamily: "Geist, system-ui, sans-serif",
              fontSize: 36,
              fontWeight: 800,
              letterSpacing: "-1.2px",
              lineHeight: 1.05,
              color: "#fff",
              marginBottom: 18,
            }}
          >
            {fleetTitle}
          </div>

          {/* Compact stats inside the header — no floating overlap cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 10,
            }}
          >
            <div
              style={{
                padding: "14px 16px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(196,179,255,0.22)",
              }}
            >
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.9px", color: "#C4B3FF", marginBottom: 8 }}>BUSES</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: "-1px", lineHeight: 1 }}>{buses.length}</span>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    fontSize: 12,
                    fontWeight: 700,
                    color: onRoute > 0 ? "#86efac" : "#A99FCE",
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: onRoute > 0 ? GREEN : "#6E6788" }} />
                  {onRoute} on route
                </span>
              </div>
            </div>

            <div
              style={{
                padding: "14px 16px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(196,179,255,0.22)",
              }}
            >
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.9px", color: "#C4B3FF", marginBottom: 8 }}>DRIVERS</div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: "-1px", lineHeight: 1 }}>{drivers.length}</span>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#A99FCE" }}>in fleet</span>
                </div>
                {drivers.length > 0 ? (
                  <div style={{ display: "flex" }}>
                    {drivers.slice(0, 3).map((d, i) => (
                      <span
                        key={d.id}
                        title={d.displayName || d.email}
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          background: "#3d2f6e",
                          border: "2px solid #241A44",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 9.5,
                          fontWeight: 800,
                          color: "#C4B3FF",
                          marginLeft: i === 0 ? 0 : -7,
                        }}
                      >
                        {initials(d.displayName || d.email)}
                      </span>
                    ))}
                    {drivers.length > 3 && (
                      <span
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          background: "#2a2048",
                          border: "2px solid #241A44",
                          display: "grid",
                          placeItems: "center",
                          fontSize: 9,
                          fontWeight: 800,
                          color: "#A99FCE",
                          marginLeft: -7,
                        }}
                      >
                        +{drivers.length - 3}
                      </span>
                    )}
                  </div>
                ) : null}
              </div>
            </div>

            <div
              style={{
                padding: "14px 16px",
                borderRadius: 14,
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(196,179,255,0.22)",
              }}
            >
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.9px", color: "#C4B3FF", marginBottom: 8 }}>PARENTS</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: pendingParentInvites > 0 ? 10 : 0, flexWrap: "wrap" }}>
                <span style={{ fontSize: 26, fontWeight: 800, color: "#fff", letterSpacing: "-1px", lineHeight: 1 }}>{parents.length}</span>
                {pendingParentInvites > 0 ? (
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#FCD34D" }}>{pendingParentInvites} invite pending</span>
                ) : (
                  <span style={{ fontSize: 12, fontWeight: 600, color: "#A99FCE" }}>joined</span>
                )}
              </div>
              {pendingParentInvites > 0 && (
                <div style={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.12)", overflow: "hidden", display: "flex" }}>
                  <span
                    style={{
                      width: `${Math.max(8, Math.round((parents.length / Math.max(1, parents.length + pendingParentInvites)) * 100))}%`,
                      background: "linear-gradient(90deg, #9B7CFF, #6F4FF0)",
                    }}
                  />
                  <span style={{ flex: 1, background: "rgba(252,211,77,0.55)" }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, width: "100%", margin: "0 auto", padding: "24px 30px 48px", position: "relative", boxSizing: "border-box" }}>
        {/* Settings grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 16, alignItems: "stretch" }}>
          {/* Signup code — no QR */}
          <div style={cardBase}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: TINT, display: "grid", placeItems: "center", fontSize: 15 }}>📌</div>
              <span style={eyebrow}>Signup code</span>
            </div>
            <p style={{ margin: "0 0 18px", fontSize: 13.5, lineHeight: 1.6, color: INK2 }}>
              Generate a 4-digit code (valid 15 min) for drivers and parents to complete signup in the mobile app.
            </p>
            {codeLive ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  padding: 16,
                  borderRadius: 16,
                  background: TINT,
                  border: "1px solid #E7DFFB",
                }}
              >
                <span
                  style={{
                    fontFamily: "Geist Mono, ui-monospace, monospace",
                    fontSize: 32,
                    fontWeight: 800,
                    letterSpacing: "0.22em",
                    lineHeight: 1,
                    color: ACCENT_TEXT,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {signupCode}
                </span>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: ACCENT }}>Expires in {formatCountdown(msLeft)}</span>
                <div style={{ height: 5, borderRadius: 99, background: "#E4DCF8", overflow: "hidden" }}>
                  <div style={{ width: `${Math.round(codeProgress * 100)}%`, height: 5, background: GRAD, transition: "width 1s linear" }} />
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: 16,
                  borderRadius: 16,
                  background: PANEL,
                  border: `1px solid ${BORDER}`,
                  fontSize: 13,
                  color: MUTED,
                  textAlign: "center",
                }}
              >
                No active code — generate one to share.
              </div>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 9, paddingTop: 16, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => void onGenerateSignupCode()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  height: 42,
                  padding: "0 20px",
                  borderRadius: 999,
                  background: GRAD,
                  border: "none",
                  boxShadow: pillGlow,
                  color: "#fff",
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {codeLive ? "New code" : "Generate code"}
              </button>
              {codeLive && (
                <button
                  type="button"
                  onClick={() => void copyCode()}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: 42,
                    padding: "0 20px",
                    borderRadius: 999,
                    background: "#fff",
                    border: `1.5px solid ${BORDER_V}`,
                    color: INK,
                    fontSize: 13.5,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              )}
            </div>
          </div>

          {/* School location. The pin the driver app fences school and depot
              arrival on. View-only unless onSaveSchoolLocation is passed, and
              Fleet settings no longer passes it: a school sets the pin while
              creating its account and after that asks Trasse (Gabriel,
              2026-10-26; fleet admins could move it from 09-25 to 10-26). */}
          <div style={cardBase}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: TINT, display: "grid", placeItems: "center", fontSize: 15 }}>📍</div>
              <span style={{ ...eyebrow, flex: 1 }}>School location</span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 22,
                  padding: "0 10px",
                  borderRadius: 999,
                  background: schoolLocation ? GREEN_BG : AMBER_BG,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: "0.6px",
                  color: schoolLocation ? GREEN_TXT : AMBER_TXT,
                }}
              >
                {schoolLocation ? "SET" : "NOT SET"}
              </span>
            </div>
            <p style={{ margin: "0 0 16px", fontSize: 13.5, lineHeight: 1.6, color: INK2 }}>
              {onSaveSchoolLocation
                ? "Where the bus counts as arrived at school and at the depot. Drivers pick up a change on their next trip."
                : "Where a bus counts as arrived at school and at the depot. To move it, contact Trasse."}
            </p>
            {(() => {
              const shown = locationDraft ?? schoolLocation;
              const editing = locationDraft !== null;
              const moved = !!(locationDraft && (!schoolLocation
                || Math.abs(locationDraft.lat - schoolLocation.lat) > 1e-7
                || Math.abs(locationDraft.lng - schoolLocation.lng) > 1e-7));
              const actionBtn = (label: string, onClick: () => void, opts?: { primary?: boolean; disabled?: boolean }): React.ReactNode => (
                <button
                  type="button"
                  onClick={onClick}
                  disabled={opts?.disabled}
                  style={{
                    height: 38,
                    padding: "0 16px",
                    borderRadius: 999,
                    border: opts?.primary ? "none" : `1.5px solid ${BORDER_V}`,
                    background: opts?.primary ? INK : "#fff",
                    color: opts?.primary ? "#fff" : INK,
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: opts?.disabled ? "default" : "pointer",
                    opacity: opts?.disabled ? 0.55 : 1,
                    fontFamily: "inherit",
                  }}
                >
                  {label}
                </button>
              );
              return shown ? (
                <>
                  <SchoolLocationMap
                    lat={shown.lat}
                    lng={shown.lng}
                    onChange={editing ? (p) => setLocationDraft(p) : undefined}
                  />
                  <div
                    style={{
                      marginTop: 12,
                      padding: "10px 12px",
                      borderRadius: 12,
                      background: PANEL,
                      border: `1px solid ${BORDER}`,
                      fontSize: 12.5,
                      color: INK2,
                      fontFamily: "Geist Mono, ui-monospace, monospace",
                    }}
                  >
                    {shown.lat.toFixed(5)}, {shown.lng.toFixed(5)}
                    <span style={{ display: "block", marginTop: 4, fontFamily: "Geist, system-ui, sans-serif", fontSize: 12, color: MUTED }}>
                      {editing
                        ? "Drag the pin, or click where the gate is. The circle is the arrival fence."
                        : `Circle is the ${CAMPUS_FENCE_RADIUS_M} m arrival fence`}
                    </span>
                  </div>
                  {onSaveSchoolLocation && (
                    <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center", flexWrap: "wrap" }}>
                      {editing ? (
                        <>
                          {actionBtn(schoolLocationSaving ? "Saving…" : "Save location", () => {
                            if (!locationDraft || schoolLocationSaving) return;
                            setLocationError(null);
                            void onSaveSchoolLocation(locationDraft)
                              .then(() => setLocationDraft(null))
                              .catch((e: unknown) => setLocationError(e instanceof Error ? e.message : "Could not save the location."));
                          }, { primary: true, disabled: !moved || schoolLocationSaving })}
                          {actionBtn("Cancel", () => { setLocationDraft(null); setLocationError(null); }, { disabled: schoolLocationSaving })}
                        </>
                      ) : (
                        actionBtn(schoolLocation ? "Change location" : "Set location", () => setLocationDraft(shown))
                      )}
                      {locationError && <span style={{ fontSize: 12.5, color: "#B42318" }}>{locationError}</span>}
                    </div>
                  )}
                </>
              ) : (
                <div
                  style={{
                    padding: 16,
                    borderRadius: 16,
                    background: PANEL,
                    border: `1px solid ${BORDER}`,
                    fontSize: 13,
                    color: MUTED,
                    textAlign: "center",
                  }}
                >
                  {onSaveSchoolLocation ? (
                    <>
                      No campus pin yet. The driver app cannot tell when a bus has reached school until one is set.
                      <div style={{ marginTop: 12 }}>
                        {/* Nairobi CBD as the starting view — the admin drags from there. */}
                        {actionBtn("Set location", () => setLocationDraft({ lat: -1.2921, lng: 36.8219 }), { primary: true })}
                      </div>
                    </>
                  ) : (
                    "No campus pin on file yet. The driver app cannot tell when a bus has reached school until one is set — contact Trasse to set it."
                  )}
                </div>
              );
            })()}
          </div>

          {showTermDates && (
            <div style={cardBase}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: TINT, display: "grid", placeItems: "center", fontSize: 15 }}>📅</div>
                <span style={eyebrow}>This term</span>
              </div>
              <p style={{ margin: "0 0 16px", fontSize: 13.5, lineHeight: 1.6, color: INK2 }}>
                The first day children came back, and the last day of this term. Fuel and route charts follow these days. Billing still follows the months you painted at signup.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end" }}>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: INK2, flex: "1 1 140px" }}>
                  Term starts
                  <input
                    type="date"
                    value={termStartDraft}
                    onChange={(e) => setTermStartDraft(e.target.value)}
                    style={{
                      fontFamily: "inherit",
                      fontSize: 14,
                      height: 44,
                      padding: "0 12px",
                      borderRadius: 12,
                      border: `1.5px solid ${BORDER_V}`,
                      background: "#fff",
                      color: INK,
                      boxSizing: "border-box",
                    }}
                  />
                </label>
                <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 700, color: INK2, flex: "1 1 140px" }}>
                  Term ends
                  <input
                    type="date"
                    value={termEndDraft}
                    onChange={(e) => setTermEndDraft(e.target.value)}
                    min={termStartDraft || undefined}
                    style={{
                      fontFamily: "inherit",
                      fontSize: 14,
                      height: 44,
                      padding: "0 12px",
                      borderRadius: 12,
                      border: `1.5px solid ${BORDER_V}`,
                      background: "#fff",
                      color: INK,
                      boxSizing: "border-box",
                    }}
                  />
                </label>
              </div>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                disabled={
                  termSaving ||
                  !onSaveTermDates ||
                  !/^\d{4}-\d{2}-\d{2}$/.test(termStartDraft) ||
                  (termEndDraft !== "" && !/^\d{4}-\d{2}-\d{2}$/.test(termEndDraft)) ||
                  (termEndDraft !== "" && termEndDraft < termStartDraft)
                }
                onClick={async () => {
                  if (!onSaveTermDates) return;
                  setTermSaving(true);
                  setTermErr(null);
                  try {
                    await onSaveTermDates(termStartDraft, termEndDraft);
                  } catch (e) {
                    setTermErr(e instanceof Error ? e.message : "Could not save.");
                  } finally {
                    setTermSaving(false);
                  }
                }}
                style={{
                  marginTop: 16,
                  alignSelf: "flex-start",
                  height: 42,
                  padding: "0 20px",
                  borderRadius: 999,
                  border: "none",
                  background: GRAD,
                  boxShadow: pillGlow,
                  color: "#fff",
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: termSaving ? "wait" : "pointer",
                  fontFamily: "inherit",
                  opacity:
                    termSaving || !/^\d{4}-\d{2}-\d{2}$/.test(termStartDraft) ? 0.55 : 1,
                }}
              >
                {termSaving ? "Saving…" : "Save term dates"}
              </button>
              {termErr ? <div style={{ marginTop: 8, fontSize: 12, color: DANGER }}>{termErr}</div> : null}
            </div>
          )}

          {/* Parent map */}
          <div style={cardBase}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: TINT, display: "grid", placeItems: "center", fontSize: 15 }}>🗺️</div>
              <span style={eyebrow}>Parent map view</span>
            </div>
            <p style={{ margin: "0 0 16px", fontSize: 13.5, lineHeight: 1.6, color: INK2 }}>
              When enabled, parents see a live map of the bus position while it is on route. When off, they see a status-only view.
            </p>
            <div style={{ display: "flex", gap: 12, flex: 1 }}>
              <ParentMapPreview mode="on" active={parentMapEnabled} />
              <ParentMapPreview mode="off" active={!parentMapEnabled} />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginTop: 16,
                padding: "14px 16px",
                borderRadius: 16,
                background: parentMapEnabled ? GREEN_BG : PANEL,
                border: `1px solid ${parentMapEnabled ? GREEN_BD : BORDER}`,
              }}
            >
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 14.5, fontWeight: 700 }}>{parentMapEnabled ? "Enabled" : "Disabled"}</span>
                <span style={{ fontSize: 12.5, color: parentMapEnabled ? GREEN_TXT : INK2 }}>
                  {parentMapEnabled ? "Parents see live map" : "Parents see status only"}
                </span>
              </div>
              <Toggle on={parentMapEnabled} disabled={parentMapSaving} onClick={() => void onToggleParentMap()} />
            </div>
          </div>

          {/* Old fuel system — per-org opt-in, off by default */}
          <div style={cardBase}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: AMBER_BG, display: "grid", placeItems: "center", fontSize: 15 }}>⛽</div>
              <span style={{ ...eyebrow, flex: 1 }}>Fuel questions</span>
              {legacyFuelFlowEnabled && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    height: 22,
                    padding: "0 10px",
                    borderRadius: 999,
                    background: AMBER_BG,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.6px",
                    color: AMBER_TXT,
                  }}
                >
                  OLD SYSTEM
                </span>
              )}
            </div>
            <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.6, color: INK2 }}>
              The Fuel Engine works out when a driver actually needs to be asked about fuel, and asks once every few days
              instead of after every trip.
            </p>
            <p style={{ margin: "10px 0 0", fontSize: 13.5, lineHeight: 1.6, color: INK2 }}>
              Turn this on only if your drivers should keep using the <strong>old fuel system</strong> instead — the survey after
              every completed route, the &ldquo;Did you refuel the vehicle?&rdquo; check on app open, and the weekend prompt.
              It replaces the Engine entirely for this fleet, and it is noisier for drivers.
            </p>
            <div style={{ flex: 1 }} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                marginTop: 18,
                padding: "14px 16px",
                borderRadius: 16,
                background: PANEL,
                border: `1px solid ${BORDER}`,
              }}
            >
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <span style={{ fontSize: 14.5, fontWeight: 700 }}>{legacyFuelFlowEnabled ? "Old fuel system" : "Fuel Engine"}</span>
                <span style={{ fontSize: 12.5, color: INK2 }}>
                  {legacyFuelFlowEnabled ? "Asks after every trip" : "Asks only when it needs to"}
                </span>
              </div>
              <Toggle
                on={legacyFuelFlowEnabled}
                disabled={tripCompleteSaving}
                onClick={() => void onToggleLegacyFuelFlow()}
                onColor="#d97706"
              />
            </div>
          </div>

          {/* Admin team */}
          <div style={cardBase}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: TINT, display: "grid", placeItems: "center", fontSize: 15 }}>⚙️</div>
              <span style={eyebrow}>Admin team</span>
            </div>
            <p style={{ margin: "0 0 14px", fontSize: 13.5, lineHeight: 1.6, color: INK2 }}>
              Invite a second admin. They&apos;ll receive an email link to set their password and access this fleet dashboard.
            </p>
            <div style={{ ...inviteScroll, maxHeight: 176 }}>
              {adminTeam.map((row) => {
                const you = row.email === meEmail;
                const subtitle =
                  row.kind === "pending"
                    ? "Pending — invite sent"
                    : row.kind === "owner"
                      ? you ? "Owner · you" : "Owner"
                      : you ? "Admin · you" : "Admin";
                return (
                  <TeamBar
                    key={row.email}
                    email={row.email}
                    subtitle={subtitle}
                    action={
                      row.kind === "pending" && onResendAdminInvite
                        ? resendBtn(row.email, () => void onResendAdminInvite(row.email))
                        : undefined
                    }
                  />
                );
              })}
            </div>
            <div style={{ flex: 1 }} />
            <div style={{ display: "flex", gap: 9, paddingTop: 14, flexWrap: "wrap" }}>
              <input
                type="email"
                value={inviteAdminEmail}
                onChange={(e) => onInviteAdminEmailChange(e.target.value)}
                placeholder="admin@organisation.co.ke"
                style={{
                  flex: 1,
                  minWidth: 160,
                  height: 44,
                  padding: "0 16px",
                  borderRadius: 999,
                  border: `1.5px solid ${BORDER_V}`,
                  fontSize: 13,
                  fontFamily: "inherit",
                  outline: "none",
                  boxSizing: "border-box",
                  background: "#fff",
                  color: INK,
                }}
              />
              <button
                type="button"
                onClick={() => void onInviteAdmin()}
                disabled={!inviteAdminEmail.trim() || adminInviteSending}
                style={{
                  height: 44,
                  padding: "0 20px",
                  borderRadius: 999,
                  background: adminInviteSending || !inviteAdminEmail.trim() ? "#c4bdd6" : GRAD,
                  border: "none",
                  boxShadow: adminInviteSending || !inviteAdminEmail.trim() ? "none" : pillGlow,
                  color: "#fff",
                  fontSize: 13.5,
                  fontWeight: 700,
                  cursor: adminInviteSending || !inviteAdminEmail.trim() ? "not-allowed" : "pointer",
                  flexShrink: 0,
                }}
              >
                {adminInviteSending ? "Sending…" : "Send invite"}
              </button>
            </div>
            {adminInviteStatus === "success" && (
              <div
                style={{
                  display: "inline-flex",
                  alignSelf: "flex-start",
                  alignItems: "center",
                  gap: 7,
                  marginTop: 11,
                  padding: "8px 14px",
                  borderRadius: 999,
                  background: GREEN_BG,
                  border: `1px solid ${GREEN_BD}`,
                  fontSize: 12,
                  fontWeight: 700,
                  color: GREEN_TXT,
                }}
              >
                Invite sent — they&apos;ll get an email link shortly
              </div>
            )}
            {adminInviteStatus === "error" && (
              <div
                style={{
                  display: "inline-flex",
                  alignSelf: "flex-start",
                  marginTop: 11,
                  padding: "8px 14px",
                  borderRadius: 999,
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  fontSize: 12,
                  color: DANGER,
                }}
              >
                {adminInviteError || "Failed to send invite. Please try again."}
              </div>
            )}
          </div>
        </div>

        {/* Invite team */}
        <div style={{ ...cardBase, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, paddingBottom: 12 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: TINT, display: "grid", placeItems: "center", fontSize: 15 }}>👥</div>
            <span style={eyebrow}>Invite team</span>
          </div>
          <p style={{ margin: "0 0 16px", fontSize: 13.5, lineHeight: 1.6, color: INK2, maxWidth: 640 }}>
            Send email invites to parents and drivers. They&apos;ll click a link, set a password, and be ready to go.
          </p>

          <div
            style={{
              display: "flex",
              gap: 4,
              marginBottom: 16,
              padding: 4,
              borderRadius: 999,
              background: PANEL,
              width: "fit-content",
            }}
          >
            {([
              { key: "parent" as const, label: "Parents" },
              { key: "bus_driver" as const, label: "Drivers" },
            ]).map((r) => {
              const active = inviteRole === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  onClick={() => onInviteRoleChange(r.key)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    height: 38,
                    padding: "0 20px",
                    borderRadius: 999,
                    border: "none",
                    background: active ? GRAD : "transparent",
                    boxShadow: active ? "0 10px 24px -12px rgba(111,79,240,0.7)" : "none",
                    color: active ? "#fff" : INK2,
                    fontSize: 13.5,
                    fontWeight: active ? 700 : 600,
                    cursor: "pointer",
                  }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {inviteEmails.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 7,
                marginBottom: 10,
                padding: 13,
                borderRadius: 16,
                background: PANEL,
                border: `1px solid ${BORDER}`,
              }}
            >
              {inviteEmails.map((em) => (
                <span
                  key={em}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    height: 28,
                    padding: "0 7px 0 13px",
                    borderRadius: 999,
                    background: TINT,
                    fontSize: 12,
                    fontWeight: 700,
                    color: ACCENT_TEXT,
                  }}
                >
                  {em}
                  <button
                    type="button"
                    onClick={() => onRemoveInviteEmail(em)}
                    aria-label={`Remove ${em}`}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#8B7DC4",
                      fontSize: 16,
                      lineHeight: 1,
                      padding: 0,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          <input
            type="email"
            value={inviteDraft}
            onChange={(e) => onInviteDraftChange(e.target.value)}
            onKeyDown={onInviteDraftKeyDown}
            placeholder={`${inviteRole === "parent" ? "Parent" : "Driver"}'s email — press Enter to add`}
            style={{
              width: "100%",
              height: 46,
              padding: "0 18px",
              borderRadius: 999,
              border: `1.5px solid ${BORDER_V}`,
              fontSize: 13.5,
              fontFamily: "inherit",
              outline: "none",
              boxSizing: "border-box",
              background: "#fff",
            }}
          />
          <div style={{ padding: "9px 4px 16px", fontSize: 11.5, color: MUTED }}>
            Press Enter after each email. Add as many as you need.
          </div>
          <button
            type="button"
            onClick={() => void onBulkInvite()}
            disabled={inviteEmails.length === 0 || bulkInviting}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 9,
              height: 46,
              padding: "0 24px",
              borderRadius: 999,
              background: inviteEmails.length === 0 || bulkInviting ? "#c4bdd6" : GRAD,
              border: "none",
              boxShadow: inviteEmails.length === 0 || bulkInviting ? "none" : pillGlow,
              color: "#fff",
              fontSize: 14,
              fontWeight: 700,
              cursor: inviteEmails.length === 0 || bulkInviting ? "not-allowed" : "pointer",
              width: "fit-content",
            }}
          >
            {bulkInviting
              ? "Sending…"
              : inviteEmails.length === 0
                ? "Add emails above to send invites"
                : `Send ${inviteEmails.length} ${inviteRole === "parent" ? "parent" : "driver"} invite${inviteEmails.length > 1 ? "s" : ""}`}
          </button>

          {inviteRows.length > 0 && (
            <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid #EDEAF6" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 12, gap: 12 }}>
                <span style={eyebrow}>{inviteRole === "parent" ? "Parent" : "Driver"} invites</span>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 120, height: 5, borderRadius: 99, background: "#EDEAF6", overflow: "hidden" }}>
                    <div style={{ width: `${joinPct}%`, height: 5, background: GRAD }} />
                  </div>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: INK2 }}>
                    {joined}/{inviteRows.length} joined
                  </span>
                </div>
              </div>
              <div style={{ ...inviteScroll, maxHeight: 248 }}>
                {inviteRows.map((r) => (
                  <TeamBar
                    key={r.id}
                    email={r.email}
                    subtitle={r.used ? "Account created" : "Pending — invite sent"}
                    action={
                      r.used
                        ? undefined
                        : resendBtn(r.email, () => void onResendInvite(r.email, r.role))
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Account */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginTop: 16,
            padding: "18px 22px",
            borderRadius: 20,
            background: "#fff",
            border: `1px solid ${BORDER}`,
            boxShadow: cardShadow,
          }}
        >
          <span
            style={{
              width: 42,
              height: 42,
              borderRadius: "50%",
              background: GRAD,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              fontSize: 14,
              fontWeight: 800,
              color: "#fff",
            }}
          >
            {avatar}
          </span>
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {userEmail || "—"}
            </span>
            <span style={{ fontSize: 12, color: MUTED }}>
              Fleet Admin · {orgDisplayName || orgCode || "—"}
            </span>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            style={{
              display: "inline-flex",
              alignItems: "center",
              height: 42,
              padding: "0 20px",
              borderRadius: 999,
              background: "#fff",
              border: `1.5px solid ${DANGER_BD}`,
              color: DANGER,
              fontSize: 13.5,
              fontWeight: 700,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
